import { Icon, IPivotItemProps, Pivot, PivotItem } from "@fluentui/react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { logEvent } from "../../helper/AppInsightsHelper";
import { IPartnerPivotProps } from "../../interfaces/PartnerView.interfaces";
import { PartnerViewTabs } from "../../Utils/Enums/Partners.constants";
import PartnerEngagementContainer from "./PartnerEngagementContainer";
import PartnerProductTelemetryView from "./PartnerProductTelemetry/PartnerProductTelemetryView";
import PartnerProfileContainer from "./PartnerProfileContainer";
import PartnerSubscriptionContainer from "./PartnerSubscriptionContainer";
import PartnerTenantDetailsContainer from "./PartnerTenantDetailsContainer";

export const PartnerPivot = (props: IPartnerPivotProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTab = "partnerProfile";

  const [selectedTab, setSelectedTab] = React.useState<string>(searchParams.get("tab") ?? defaultTab);

  const TABS_DATA = {
    pivot: { ariaLabel: "customer-pivot-component-aria-label" },
    pivotItems: [
      {
        headerText: "Partner Profile",
        tooltipText: "Please find this tab information on all Partners associated with the selected PartnerOne",
        itemKey: PartnerViewTabs.PartnerProfile,
        component: (
          <PartnerProfileContainer
            partnerOneName={props.partnerOneName}
            partnerOneID={props.partnerOneID}
            mpnId={props.mpnId}
          />
        )
      },
      {
        headerText: "Tenant Profile",
        tooltipText: "Please find this tab information on all Tenants associated with the Partner",
        itemKey: PartnerViewTabs.TenantDetails,
        component: (
          <PartnerTenantDetailsContainer
            partnerOneName={props.partnerOneName}
            partnerOneID={props.partnerOneID}
            mpnId={props.mpnId}
          />
        )
      },
      {
        headerText: "License And Subscription",
        tooltipText: "License and Subscriptions associated with the Partner through CSP",
        itemKey: PartnerViewTabs.Subscription,
        component: (
          <PartnerSubscriptionContainer
            partnerOneName={props.partnerOneName}
            partnerOneID={props.partnerOneID}
            mpnId={props.mpnId}
          />
        )
      },
      {
        headerText: "Product Telemetry",
        tooltipText: "Please find this tab information on Product Telemetry associated with the Partner",
        itemKey: PartnerViewTabs.ProductTelemetry,
        component: (
          <PartnerProductTelemetryView
            partnerOneName={props.partnerOneName}
            partnerOneID={props.partnerOneID}
            mpnId={props.mpnId}
          />
        )
      },
      {
        headerText: "Engagements",
        tooltipText: "Please find this tab information on Engagements associated with the Partner",
        itemKey: PartnerViewTabs.Engagements,
        component: (
          <PartnerEngagementContainer
            partnerOneName={props.partnerOneName}
            partnerOneID={props.partnerOneID}
            mpnId={props.mpnId}
          />
        )
      }
    ]
  };

  const _customIconRenderer = (
    tooltipText: string,
    link?: IPivotItemProps,
    defaultRenderer?: (link?: IPivotItemProps) => React.JSX.Element | null
  ): React.JSX.Element | null => {
    if (!link || !defaultRenderer) {
      return null;
    }

    return (
      <span style={{ flex: "0 1 100%" }}>
        {defaultRenderer({ ...link, itemIcon: undefined })}
        <Icon iconName={link.itemIcon} style={{ color: "#006CBE", margin: "3px",fontSize: "1rem" }} title={tooltipText} />
      </span>
    );
  };

  const renderPartnerViewTabs = TABS_DATA.pivotItems.map(({ headerText, tooltipText, itemKey, component }, index) => (
    <PivotItem
      headerText={headerText}
      itemKey={itemKey}
      key={`${itemKey + index}`}
      itemIcon="Unknown"
      onRenderItemLink={_customIconRenderer.bind(null, tooltipText)}
    >
      {component}
    </PivotItem>
  ));

  const handleSelectedTab = (item?: PivotItem) => {
    if (item) {
      const newTab = item.props.itemKey as PartnerViewTabs; // Ensure newTab is string
      const currentId = searchParams.get("partnerOneId"); // Get current 'id'
      const mpnID = searchParams.get("mpnId");
      setSearchParams({ partnerOneId: currentId || "", mpnId: mpnID || "", tab: newTab });

      const customPropsForLogging = {
        SelectedTab: newTab
      };
      logEvent("PartnerPage tab selected", "PartnerPage", "PartnerPivot", customPropsForLogging);
    }
  };

  React.useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && Object.values(PartnerViewTabs).includes(tab as PartnerViewTabs)) {
      setSelectedTab(tab);
    } else {
      setSelectedTab(defaultTab); // Default to Tabs.Product
    }
  }, [searchParams]);

  return (
    <Pivot aria-label={TABS_DATA.pivot.ariaLabel} onLinkClick={handleSelectedTab} selectedKey={selectedTab}>
      {renderPartnerViewTabs}
    </Pivot>
  );
};
